import { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import {Header} from './components/Header';
import {TmsTaskCardItem} from './components/TmsTaskCardItem';
import {ShipFilter} from './components/ShipFilter';
import { PageState } from './components/PageState';
import PullToRefreshWrapper from '../../components/PullToRefreshWrapper';
import {t} from '../../i18n';
import { GET_ACCOUNT_BIND_WAREHOUSE, GetAccountBindWarehouseResponse } from '../../graphql/queries/warehouseQueries';
import {
    GET_TMS_WAREHOUSE_OPERATION_SHIP_OUT_TASK_LIST,
    GetTmsWarehouseOperationShipOutTaskListResponse
} from '../../graphql/queries/tmsTaskWarehouseOperationQueries';
import { Pda } from './components/Pda';

interface PageBodyProps {
    warehouseId?: number;
    searchText?: string;
}

const PageBody = ({ warehouseId, searchText }: PageBodyProps) => {
    const { data: queryData, loading, error, refetch } = useQuery<GetTmsWarehouseOperationShipOutTaskListResponse>(
        GET_TMS_WAREHOUSE_OPERATION_SHIP_OUT_TASK_LIST,
        {
            variables: {
                warehouseId,
                searchText
            },
            skip: !warehouseId
        }
    );

    const tasks = queryData?.getTmsWarehouseOperationShipOutTaskList || [];

    const handleRefresh = async () => {
        await refetch();
    };

    if (loading) {
        return <PageState />;
    }

    if (error) {
        return <PageState state="error" onRetry={() => refetch()} />;
    }

    if (tasks.length === 0) {
        return <PageState state="empty" />;
    }

    return (
        <PullToRefreshWrapper
            onRefresh={handleRefresh}
            pullingText={t('pda.pages.shipOut.pullToRefresh')}
            canReleaseText={t('pda.pages.shipOut.canRelease')}
            refreshingText={t('pda.pages.shipOut.refreshing')}
            completeText={t('pda.pages.shipOut.complete')}
        >
            <div className='w-full h-screen bg-gray-50'>
                <div className="px-3 bg-gray-50 flex flex-col gap-3">
                    {tasks.map((task) => (
                        <TmsTaskCardItem
                            key={task.taskId}
                            task={task}
                        />
                    ))}
                    {/* 占位符，保持底部不贴边 */}
                    <div className="h-0" />
                </div>
            </div>
        </PullToRefreshWrapper>
    );
};

export default function ShipoutPage() {
    const SHIP_OUT_FILTER_STORAGE_KEY = 'pda_ship_out_filters_v1';
    const FILTER_EXPIRE_MS = 24 * 60 * 60 * 1000;

    const { data, loading: warehouseLoading } = useQuery<GetAccountBindWarehouseResponse>(GET_ACCOUNT_BIND_WAREHOUSE, {
        fetchPolicy: 'cache-and-network'
    });

    const warehouseId = data?.warehouse?.id;

    const [searchText, setSearchText] = useState<string>();

    useEffect(() => {
        try {
            const raw = window.localStorage.getItem(SHIP_OUT_FILTER_STORAGE_KEY);
            if (!raw) return;
            const parsed = JSON.parse(raw) as { v?: number; updatedAt?: number; searchText?: string };
            const updatedAt = parsed?.updatedAt;
            if (!updatedAt || Date.now() - updatedAt > FILTER_EXPIRE_MS) return;
            setSearchText(parsed.searchText);
        } catch {
            // ignore: localStorage unavailable
        }
    }, []);

    const handleSearch = (filters: { searchText?: string }) => {
        setSearchText(filters.searchText);

        try {
            window.localStorage.setItem(
                SHIP_OUT_FILTER_STORAGE_KEY,
                JSON.stringify({
                    v: 1,
                    updatedAt: Date.now(),
                    searchText: filters.searchText,
                })
            );
        } catch {
            // ignore: localStorage unavailable
        }
    };

    if (warehouseLoading) {
        return <PageState withHeader headerTitle={t('pda.pages.shipOut.shipOut')} />;
    }

    return (
        <div className="w-full h-screen flex flex-col pt-2">
            <div className="h-0 overflow-hidden"><Pda /></div>
            <Header title={t('pda.pages.shipOut.shipOut')}/>
            <ShipFilter
                onSearch={handleSearch}
                searchText={searchText}
            />
            <PageBody
                warehouseId={warehouseId}
                searchText={searchText}
            />
        </div>
    );
}
